/**
 * Navbar Component - Main navigation bar
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, User, LogOut, Menu, X, Bell, Gift, HelpCircle } from "lucide-react";
import { useAuth } from "@/App";
import api from "@/lib/api";

// Tutorial Button Component
const TutorialButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-2 py-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-800/50 rounded-lg transition-colors"
    title="View Tutorial"
  >
    <HelpCircle className="w-4 h-4" />
  </button>
);

// Notification Bell Component
const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get("/notifications?limit=5");
        setNotifications(res.data.notifications || []);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-slate-400 hover:text-cyan-400 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
            {unreadCount}
          </span>
        )}
      </button>
      
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50">
          <div className="p-3 border-b border-slate-700">
            <h4 className="text-white font-semibold">Notifications</h4>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-4 text-slate-500 text-center text-sm">No notifications</p>
            ) : (
              notifications.map((n, i) => (
                <div key={i} className={`p-3 border-b border-slate-800 ${!n.is_read ? 'bg-cyan-500/5' : ''}`}>
                  <p className="text-white text-sm font-medium">{n.title}</p>
                  <p className="text-slate-400 text-xs">{n.message}</p>
                </div>
              ))
            )}
          </div>
          <Link to="/notifications" className="block p-3 text-center text-cyan-400 text-sm hover:bg-slate-800">
            View All
          </Link>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ TutorialModal, ReferralInviteModal }) => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (user) {
        try {
          const res = await api.get("/admin/stats");
          setIsSuperAdmin(res.data.is_super_admin || false);
        } catch {
          setIsSuperAdmin(false);
        }
      } else {
        setIsSuperAdmin(false);
      }
    };
    checkSuperAdmin();
  }, [user]);

  useEffect(() => {
    if (user) {
      const tutorialCompleted = localStorage.getItem('tutorial_public_completed');
      if (!tutorialCompleted) {
        setShowTutorial(true);
      }
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
      localStorage.removeItem("wysspr_token");
      setUser(null);
      navigate("/");
      toast.success("Logged out successfully");
    } catch {
      toast.error("Logout failed");
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Shield className="w-8 h-8 text-cyan-500" />
            <span className="font-heading font-bold text-xl tracking-wider text-white">WYSSPR</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                <Link to="/dashboard" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/dashboard') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Dashboard
                </Link>
                <Link to="/tips" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/tips') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Tips
                </Link>
                <Link to="/bounty" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/bounty') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Bounty
                </Link>
                <Link to="/rewards" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/rewards') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Rewards
                </Link>
                <Link to="/chat" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/chat') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Wysspr AI
                </Link>
                <Link to="/community" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/community') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Community
                </Link>
                <Link to="/map" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/map') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Map
                </Link>
                {isSuperAdmin && (
                  <Link to="/superadmin" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/superadmin') ? 'text-amber-400 bg-amber-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                    Admin
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/map" className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wide rounded transition-colors ${isActive('/map') ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
                  Map
                </Link>
                <a href="#how-it-works" className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors">How It Works</a>
                <a href="#about" className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors">About</a>
                <a href="#agencies" className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded transition-colors">Agencies</a>
              </>
            )}
          </div>

          {/* User Section */}
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <>
                <button 
                  onClick={() => setShowReferralModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-400 hover:text-purple-300 rounded-lg transition-colors text-xs font-medium"
                >
                  <Gift className="w-3.5 h-3.5" />
                  <span>Refer a Friend</span>
                </button>
                <TutorialButton onClick={() => setShowTutorial(true)} />
                <NotificationBell />
                <Link to="/profile" className="flex items-center gap-1.5 text-slate-400 hover:text-cyan-400 transition-colors">
                  <User className="w-4 h-4" />
                  <span className="text-xs font-medium">{user.name?.split(' ')[0]}</span>
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="flex items-center gap-1 px-2 py-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors text-xs"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-secondary text-sm px-4 py-2">LOGIN</Link>
                <Link to="/login" className="btn-primary text-sm px-4 py-2" onClick={() => {}}>GET STARTED</Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-slate-400" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-slate-900 border-t border-slate-800"
          >
            <div className="px-4 py-4 space-y-2">
              {user ? (
                <>
                  <Link to="/dashboard" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Dashboard</Link>
                  <Link to="/tips" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Tips</Link>
                  <Link to="/bounty" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Bounty</Link>
                  <Link to="/community" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Community</Link>
                  <Link to="/profile" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Profile</Link>
                  <button 
                    onClick={() => { setMobileOpen(false); setShowReferralModal(true); }}
                    className="w-full text-left px-3 py-2 text-purple-400 hover:bg-purple-500/10 rounded flex items-center gap-2"
                  >
                    <Gift className="w-4 h-4" /> Refer a Friend
                  </button>
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-red-400 hover:bg-red-500/10 rounded flex items-center gap-2">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/map" className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded" onClick={() => setMobileOpen(false)}>Map</Link>
                  <Link to="/login" className="block px-3 py-2 text-cyan-400 hover:bg-cyan-500/10 rounded" onClick={() => setMobileOpen(false)}>Login</Link>
                  <Link to="/login" className="block px-3 py-2 bg-cyan-500 text-black rounded font-medium text-center" onClick={() => setMobileOpen(false)}>Get Started</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      {TutorialModal && <TutorialModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} tutorialType="public" />}
      {ReferralInviteModal && <ReferralInviteModal isOpen={showReferralModal} onClose={() => setShowReferralModal(false)} />}
    </nav>
  );
};

export default Navbar;
